// Song waveform for the llama player: tinted by the song's audience colour, tap to jump, start-point flags on top.
import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { clock } from "./sampler";

const H = 46;

const draw = (canvas, w, peaks, progress, color, clip) => {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(H * dpr);
  const g = canvas.getContext("2d");
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, H);
  const bars = Math.max(24, Math.floor(w / 3));
  const per = peaks.length / bars;
  g.fillStyle = color;
  for (let i = 0; i < bars; i++) {
    let m = 0;
    for (let j = Math.floor(i * per); j < Math.max(Math.floor((i + 1) * per), Math.floor(i * per) + 1); j++) m = Math.max(m, peaks[j] || 0);
    const frac = (i + 0.5) / bars;
    const inClip = !clip || (frac >= clip[0] && frac <= clip[1]);
    g.globalAlpha = (frac <= progress ? 1 : 0.38) * (inClip ? 1 : 0.3);
    const h = Math.max(2, m * (H - 6));
    g.fillRect(i * 3, (H - h) / 2, 2, h);
  }
};

export const Waveform = ({ peaks, duration, time, color, cues = [], clip, pinMode, onSeek, onAddCue, onCue }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const [w, setW] = useState(320);
  useEffect(() => {
    const el = wrap.current;
    if (!el || !("ResizeObserver" in window)) return undefined;
    const ro = new ResizeObserver(() => setW(el.clientWidth || 320));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const progress = duration ? time / duration : 0;
  const clipFrac = clip && duration ? [clip[0] / duration, clip[1] / duration] : null;
  const step = Math.round(progress * 400);
  useEffect(() => {
    if (canvas.current && peaks?.length) draw(canvas.current, w, peaks, progress, color, clipFrac);
  }, [peaks, w, step, color, clipFrac?.[0], clipFrac?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (e) => {
    if (!duration) return;
    const r = wrap.current.getBoundingClientRect();
    const sec = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * duration;
    if (pinMode) onAddCue?.(sec); else onSeek?.(sec);
  };
  return (
    <div ref={wrap} onClick={pick} className={`relative w-full select-none ${pinMode ? "cursor-crosshair" : "cursor-pointer"}`} style={{ height: H }} data-testid="player-waveform" title={pinMode ? "Tap to add a start point here" : "Tap to jump"}>
      <canvas ref={canvas} style={{ width: w, height: H, display: "block" }} />
      {pinMode && <span className="absolute inset-0 rounded border border-dashed border-white/40 pointer-events-none" />}
      {duration > 0 && cues.map((c, i) => (
        <button
          key={`${c.at}-${i}`}
          type="button"
          onClick={(e) => { e.stopPropagation(); onCue?.(c.at); }}
          style={{ left: `${Math.min(100, (c.at / duration) * 100)}%` }}
          className="absolute top-0 h-full w-4 -translate-x-1/2 flex justify-center group"
          title={`${c.starred ? "Favourite start" : "Start point"} · ${clock(c.at)}`}
          aria-label={`Jump to ${clock(c.at)}`}
          data-testid={`waveform-cue-${i}`}
        >
          <span className={`h-full w-[2px] ${c.starred ? "bg-white" : "bg-white/55"} group-hover:bg-white`} />
          {c.starred
            ? <Star className="absolute -top-1.5 h-3 w-3 fill-white text-white drop-shadow" />
            : <span className="absolute -top-1 h-2 w-2 rounded-full bg-white/80" />}
        </button>
      ))}
    </div>
  );
};
